import { StyleSheet } from 'react-native'

const colorPalette = {
  primary: '#0B495C',
  secondary: '#4FC7E8',
  tertiary: '#007AB7', // rgba(0, 122, 183, 1)
  dark: '#43525A', // rgba(67, 82, 90, 1)
  light: '#EFEFEF', // rgba(239, 239, 239, 1)
  lighter: '#f2f2f2',
  darkLight: '#DDDDDD', // rgba(221, 221, 221, 1)
  gray: '#7F7F7F',
  white: '#FFFFFF', // rgba(255, 255, 255, 1)
  black: '#1D1D1D', // rgba(29, 29, 29, 1)
  alert: '#8E1CD8', // rgba(142, 28, 216, 1)
  orange: '#ED8B22',
  green: '#9DD086',
  red: '#D81616', // rgba(216, 22, 22, 1)
  spray: '#76D8D4', // rgba(118, 216, 212, 1)
  lightGray: '#CCCCCC', // rgba(204, 204, 204, 1)
  hippieBlue: '#4E8EAA', // rgba(78, 142, 170, 1)
  lightBlue: '#EAF2F5', // rgba(234, 242, 245, 1)
  lightSecondary: 'rgba(0, 153, 229, .2)', //
  lightDark: 'rgba(67, 82, 90, .2)',
  transparent: 'transparent',
  notStarted: '#7F7F7F',
  skyBlue: '#4fc7e8',
  violet: '#A84499'
}

const fontPalette = {
  primaryLight: 'BaiJamjuree_300Light',
  primaryLightItalic: 'BaiJamjuree_300Light_Italic',
  primaryRegular: 'BaiJamjuree_400Regular',
  primaryRegularItalic: 'BaiJamjuree_400Regular_Italic',
  primarySemiBold: 'BaiJamjuree_600SemiBold',
  primaryBold: 'BaiJamjuree_700Bold'
}

const globalStyles = StyleSheet.create({
  // container
  container: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: colorPalette.lighter
  },
  mediumContainer: {
    width: 310
  },
  smallContainer: {
    width: 250
  },

  // overlap
  containerBand: {
    minHeight: 140,
    paddingTop: 15,
    paddingBottom: 50
  },
  containerBandNarrow: {
    minHeight: 40
  },
  containerBandMenu: {
    minHeight: 0,
    paddingTop: 15,
    paddingBottom: 13
  },
  containerOverlap: {
    marginTop: -44,
    padding: 16
  },
  containerOverlapInner: {
    backgroundColor: colorPalette.white,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0
  },
  containerOverlapNoGap: {
    padding: 0
  },
  containerOverlapNoHGap: {
    paddingHorizontal: 0
  },
  containerOverlapNoVGap: {
    paddingVertical: 0
  },
  textOverlap: {
    marginHorizontal: 17,
    marginTop: 26,
    marginBottom: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },

  // color
  primaryTextColor: {
    color: colorPalette.primary
  },
  primaryBgColor: {
    backgroundColor: colorPalette.primary
  },
  secondaryTextColor: {
    color: colorPalette.secondary
  },
  secondaryBgColor: {
    backgroundColor: colorPalette.secondary
  },
  tertiaryTextColor: {
    color: colorPalette.tertiary
  },
  tertiaryBgColor: {
    backgroundColor: colorPalette.tertiary
  },
  darkTextColor: {
    color: colorPalette.dark
  },
  darkBgColor: {
    backgroundColor: colorPalette.dark
  },
  grayTextColor: {
    color: colorPalette.gray
  },
  grayBgColor: {
    backgroundColor: colorPalette.gray
  },
  lightGrayTextColor: {
    color: colorPalette.lightGray
  },
  lightGrayBgColor: {
    backgroundColor: colorPalette.lightGray
  },
  lightTextColor: {
    color: colorPalette.light
  },
  lightBgColor: {
    backgroundColor: colorPalette.light
  },
  lighterTextColor: {
    color: colorPalette.lighter
  },
  lighterBgColor: {
    backgroundColor: colorPalette.lighter
  },
  whiteTextColor: {
    color: colorPalette.white
  },
  whiteBgColor: {
    backgroundColor: colorPalette.white
  },
  alertTextColor: {
    color: colorPalette.alert
  },
  alertBgColor: {
    backgroundColor: colorPalette.alert
  },
  blackTextColor: {
    color: colorPalette.black
  },
  blackBgColor: {
    backgroundColor: colorPalette.black
  },
  dangerTextColor: {
    color: colorPalette.red
  },
  dangerBgColor: {
    backgroundColor: colorPalette.red
  },
  warnTextColor: {
    color: colorPalette.orange
  },
  warnBgColor: {
    backgroundColor: colorPalette.orange
  },
  successTextColor: {
    color: colorPalette.green
  },
  successBgColor: {
    backgroundColor: colorPalette.green
  },
  transparentBg: {
    backgroundColor: colorPalette.transparent
  },
  textCenter: {
    textAlign: 'center'
  },
  textUnderline: {
    textDecorationLine: 'underline'
  },
  textUppercase: {
    textTransform: 'uppercase'
  },
  textLowecase: {
    textTransform: 'lowercase'
  },
  relativePosition: {
    position: 'relative'
  },
  absolutePosition: {
    position: 'absolute'
  },
  horizontalPadGap: {
    paddingHorizontal: 16
  },
  verticalPadGap: {
    paddingVertical: 16
  },
  horizontalMarGap: {
    marginHorizontal: 16
  },
  verticalMarGap: {
    marginVertical: 16
  },

  // typography
  text: {
    fontSize: 14,
    lineHeight: 19,
    fontFamily: fontPalette.primaryRegular,
    color: colorPalette.dark
  },
  fwLight: {
    fontFamily: fontPalette.primaryLight
  },
  fwLightItalic: {
    fontFamily: fontPalette.primaryLightItalic
  },
  fwRegular: {
    fontFamily: fontPalette.primaryRegular
  },
  fwRegularItalic: {
    fontFamily: fontPalette.primaryRegularItalic
  },
  fwSemiBold: {
    fontFamily: fontPalette.primarySemiBold
  },
  fwBold: {
    fontFamily: fontPalette.primaryBold
  },
  fs10: {
    fontSize: 10,
    lineHeight: 15
  },
  fs11: {
    fontSize: 11,
    lineHeight: 16
  },
  fs12: {
    fontSize: 12,
    lineHeight: 17
  },
  fs13: {
    fontSize: 13,
    lineHeight: 18
  },
  fs14: {
    fontSize: 14,
    lineHeight: 19
  },
  fs15: {
    fontSize: 15,
    lineHeight: 20
  },
  fs16: {
    fontSize: 16,
    lineHeight: 21
  },
  fs18: {
    fontSize: 18,
    lineHeight: 23
  },
  fs20: {
    fontSize: 20,
    lineHeight: 25
  },
  fs22: {
    fontSize: 22,
    lineHeight: 27
  },
  fs24: {
    fontSize: 24,
    lineHeight: 29
  },
  fs26: {
    fontSize: 26,
    lineHeight: 31
  },
  fs28: {
    fontSize: 28,
    lineHeight: 33
  },
  fs30: {
    fontSize: 30,
    lineHeight: 35
  },
  lh10: { lineHeight: 10 },
  lh11: { lineHeight: 11 },
  lh12: { lineHeight: 12 },
  lh13: { lineHeight: 13 },
  lh14: { lineHeight: 14 },
  lh15: { lineHeight: 15 },
  lh16: { lineHeight: 16 },
  lh18: { lineHeight: 18 },
  lh20: { lineHeight: 20 },
  lh22: { lineHeight: 22 },
  lh24: { lineHeight: 24 },
  lh26: { lineHeight: 26 },
  lh28: { lineHeight: 28 },
  lh30: { lineHeight: 30 },

  // aligned items
  flexNone: {
    flex: 0
  },
  flexOne: {
    flex: 1
  },
  hDirection: {
    flexDirection: 'row'
  },
  hAlignItems: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start'
  },
  hBetweenItems: {
    justifyContent: 'space-between'
  },
  hStartItems: {
    justifyContent: 'flex-start'
  },
  hEndItems: {
    justifyContent: 'flex-end'
  },
  hCenterItems: {
    justifyContent: 'center'
  },

  vDirection: {
    flexDirection: 'column'
  },
  vAlignItems: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  vStartItems: {
    alignItems: 'flex-start'
  },
  vEndItems: {
    alignItems: 'flex-end'
  },
  vCenterItems: {
    alignItems: 'center'
  },

  hvCenterItems: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },

  flexWrapped: {
    flexWrap: 'wrap'
  },
  flexNoWrapped: {
    flexWrap: 'wrap'
  },
  flexVerseWrapped: {
    flexWrap: 'wrap-reverse'
  },

  alignSelfLeft: {
    alignSelf: 'flex-start'
  },
  alignSelfCenter: {
    alignSelf: 'center'
  },
  alignSelfRight: {
    alignSelf: 'flex-end'
  },

  // button
  button: {
    borderRadius: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
    height: 40,
    backgroundColor: colorPalette.white,
    borderColor: colorPalette.white
  },
  buttonLarge: {
    height: 60,
    paddingVertical: 14
  },
  buttonRouded: {
    borderRadius: 50
  },
  buttonDisable: {
    opacity: 0.4
  },

  // form
  input: {
    borderWidth: 1,
    backgroundColor: colorPalette.white,
    borderColor: colorPalette.gray,
    color: colorPalette.dark,
    paddingHorizontal: 12,
    height: 40,
    fontFamily: fontPalette.primaryRegular,
    fontSize: 15,
    lineHeight: 20,
    borderRadius: 8,
    marginVertical: 4
  },
  inputError: {
    borderColor: colorPalette.red
  },
  inputLabel: {
    color: colorPalette.gray,
    fontSize: 12,
    marginBottom: 0,
    marginHorizontal: 1
  },
  inputLabelLarge: {
    fontFamily: fontPalette.primaryRegular,
    fontSize: 14
  },
  inputLabelPrimary: {
    color: colorPalette.primary
  },
  inputLabelSecondary: {
    color: colorPalette.secondary
  },
  inputLight: {
    borderColor: colorPalette.lightGray
  },
  inputSelect: {
    lineHeight: 36,
    paddingRight: 35
  },
  inputReadOnly: {
    backgroundColor: 'rgba(0, 0, 0, .04)'
  },
  textarea: {
    textAlignVertical: 'top',
    width: '100%',
    height: 100,
    paddingTop: 10,
    paddingBottom: 10
  },
  checkbox: {
    width: 15,
    height: 15,
    borderWidth: 1,
    borderColor: colorPalette.gray,
    backgroundColor: colorPalette.white
  },
  checkboxOn: {
    backgroundColor: colorPalette.secondary,
    borderColor: colorPalette.secondary
  },
  inputWithIconWrapper: {
    position: 'relative',
    marginHorizontal: 0
  },
  inputWithIcon: {
    position: 'absolute',
    bottom: 11
  },
  inputWithIconStart: { left: 8 },
  inputWithIconEnd: { right: 8 },

  // alerts
  dangerAlert: {
    fontFamily: fontPalette.primaryRegular,
    fontSize: 12,
    color: colorPalette.red
  },

  // brand
  brandAuthWrapper: {
    marginTop: 30
  },
  brand: {
    width: 80,
    resizeMode: 'contain',
    aspectRatio: 1,
    marginTop: 20,
    borderRadius: 10
  },

  // card
  card: {
    backgroundColor: colorPalette.white,
    borderRadius: 8,
    paddingVertical: 18,
    paddingHorizontal: 20
  },
  cardNarow: {
    backgroundColor: colorPalette.white,
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 20
  },
  carNoGap: {
    padding: 0
  },
  cardGap: {
    paddingVertical: 18,
    paddingHorizontal: 20
  },
  shadowPop: {
    shadowColor: colorPalette.dark,
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 6
  },

  // badge
  badge: {
    borderRadius: 15,
    backgroundColor: colorPalette.white,
    paddingHorizontal: 14,
    paddingTop: 2,
    paddingBottom: 3,
    minWidth: 100
  },
  badgeFlat: {
    paddingHorizontal: 2,
    minWidth: 'auto'
  },
  badgeText: {
    fontSize: 14,
    lineHeight: 19,
    paddingVertical: 1,
    paddingHorizontal: 5,
    fontFamily: fontPalette.primaryRegular,
    color: colorPalette.dark,
    textAlign: 'center'
  },
  badgeNotStarted: {
    backgroundColor: colorPalette.notStarted
  },
  badgeTextNotStarted: {
    color: colorPalette.white
  },
  badgeIncompleted: {
    backgroundColor: colorPalette.tertiary
  },
  badgeTextIncompleted: {
    color: colorPalette.white
  },
  badgePending: {
    backgroundColor: colorPalette.orange
  },
  badgeTextPending: {
    color: colorPalette.white
  },
  badgeResubmit: {
    backgroundColor: colorPalette.red
  },
  badgeTextResubmit: {
    color: colorPalette.white
  },
  badgeCompleted: {
    backgroundColor: colorPalette.green
  },
  badgeTextCompleted: {
    color: colorPalette.white
  },
  badgeVerify: {
    backgroundColor: colorPalette.tertiary
  },
  badgeTextVerify: {
    color: colorPalette.white
  },

  // utils
  w0: { width: '0%' },
  w10: { width: '10%' },
  w20: { width: '20%' },
  w30: { width: '30%' },
  w40: { width: '40%' },
  w50: { width: '50%' },
  w60: { width: '60%' },
  w70: { width: '70%' },
  w80: { width: '80%' },
  w85: { width: '85%' },
  w90: { width: '90%' },
  w95: { width: '95%' },
  w100: { width: '100%' },
  mt0: {
    marginTop: 0
  },
  mt1: {
    marginTop: 5
  },
  mt2: {
    marginTop: 10
  },
  mt3: {
    marginTop: 15
  },
  mt4: {
    marginTop: 20
  },
  mt5: {
    marginTop: 25
  },
  mt6: {
    marginTop: 30
  },
  mt7: {
    marginTop: 35
  },
  mt8: {
    marginTop: 40
  },
  mb0: {
    marginBottom: 0
  },
  mb1: {
    marginBottom: 5
  },
  mb2: {
    marginBottom: 10
  },
  mb3: {
    marginBottom: 15
  },
  mb4: {
    marginBottom: 20
  },
  mb5: {
    marginBottom: 25
  },
  mb6: {
    marginBottom: 30
  },
  mb7: {
    marginBottom: 35
  },
  mb8: {
    marginBottom: 40
  },
  ml0: {
    marginLeft: 0
  },
  ml1: {
    marginLeft: 5
  },
  ml2: {
    marginLeft: 10
  },
  ml3: {
    marginLeft: 15
  },
  ml4: {
    marginLeft: 20
  },
  ml5: {
    marginLeft: 25
  },
  ml6: {
    marginLeft: 30
  },
  ml7: {
    marginLeft: 35
  },
  ml8: {
    marginLeft: 40
  },
  mr0: {
    marginRight: 0
  },
  mr1: {
    marginRight: 5
  },
  mr2: {
    marginRight: 10
  },
  mr3: {
    marginRight: 15
  },
  mr4: {
    marginRight: 20
  },
  mr5: {
    marginRight: 25
  },
  mr6: {
    marginRight: 30
  },
  mr7: {
    marginRight: 35
  },
  mr8: {
    marginRight: 40
  },
  pt0: {
    paddingTop: 0
  },
  pt1: {
    paddingTop: 5
  },
  pt2: {
    paddingTop: 10
  },
  pt3: {
    paddingTop: 15
  },
  pt4: {
    paddingTop: 20
  },
  pt5: {
    paddingTop: 25
  },
  pt6: {
    paddingTop: 30
  },
  pt7: {
    paddingTop: 35
  },
  pt8: {
    paddingTop: 40
  },
  pb0: {
    paddingBottom: 0
  },
  pb1: {
    paddingBottom: 5
  },
  pb2: {
    paddingBottom: 10
  },
  pb3: {
    paddingBottom: 15
  },
  pb4: {
    paddingBottom: 20
  },
  pb5: {
    paddingBottom: 25
  },
  pb6: {
    paddingBottom: 30
  },
  pb7: {
    paddingBottom: 35
  },
  pb8: {
    paddingBottom: 40
  },
  pl0: {
    paddingLeft: 0
  },
  pl1: {
    paddingLeft: 5
  },
  pl2: {
    paddingLeft: 10
  },
  pl3: {
    paddingLeft: 15
  },
  pl4: {
    paddingLeft: 20
  },
  pl5: {
    paddingLeft: 25
  },
  pl6: {
    paddingLeft: 30
  },
  pl7: {
    paddingLeft: 35
  },
  pl8: {
    paddingLeft: 40
  },
  pr0: {
    paddingRight: 0
  },
  pr1: {
    paddingRight: 5
  },
  pr2: {
    paddingRight: 10
  },
  pr3: {
    paddingRight: 15
  },
  pr4: {
    paddingRight: 20
  },
  pr5: {
    paddingRight: 25
  },
  pr6: {
    paddingRight: 30
  },
  pr7: {
    paddingRight: 35
  },
  pr8: {
    paddingRight: 40
  },
  lp1: { left: 1 },
  lp2: { left: 2 },
  lp3: { left: 3 },
  lp4: { left: 4 },
  lp5: { left: 5 },
  lp6: { left: 6 },
  lp7: { left: 7 },
  lp8: { left: 8 },
  lp9: { left: 9 },
  lp10: { left: 10 },
  ln1: { left: -1 },
  ln2: { left: -2 },
  ln3: { left: -3 },
  ln4: { left: -4 },
  ln5: { left: -5 },
  ln6: { left: -6 },
  ln7: { left: -7 },
  ln8: { left: -8 },
  ln9: { left: -9 },
  ln10: { left: -10 },
  rp1: { right: 1 },
  rp2: { right: 2 },
  rp3: { right: 3 },
  rp4: { right: 4 },
  rp5: { right: 5 },
  rp6: { right: 6 },
  rp7: { right: 7 },
  rp8: { right: 8 },
  rp9: { right: 9 },
  rp10: { right: 10 },
  rn1: { right: -1 },
  rn2: { right: -2 },
  rn3: { right: -3 },
  rn4: { right: -4 },
  rn5: { right: -5 },
  rn6: { right: -6 },
  rn7: { right: -7 },
  rn8: { right: -8 },
  rn9: { right: -9 },
  rn10: { right: -10 },
  tp1: { top: 1 },
  tp2: { top: 2 },
  tp3: { top: 3 },
  tp4: { top: 4 },
  tp5: { top: 5 },
  tp6: { top: 6 },
  tp7: { top: 7 },
  tp8: { top: 8 },
  tp9: { top: 9 },
  tp10: { top: 10 },
  tn1: { top: -1 },
  tn2: { top: -2 },
  tn3: { top: -3 },
  tn4: { top: -4 },
  tn5: { top: -5 },
  tn6: { top: -6 },
  tn7: { top: -7 },
  tn8: { top: -8 },
  tn9: { top: -9 },
  tn10: { top: -10 },
  bp1: { bottom: 1 },
  bp2: { bottom: 2 },
  bp3: { bottom: 3 },
  bp4: { bottom: 4 },
  bp5: { bottom: 5 },
  bp6: { bottom: 6 },
  bp7: { bottom: 7 },
  bp8: { bottom: 8 },
  bp9: { bottom: 9 },
  bp10: { bottom: 10 },
  bn1: { bottom: -1 },
  bn2: { bottom: -2 },
  bn3: { bottom: -3 },
  bn4: { bottom: -4 },
  bn5: { bottom: -5 },
  bn6: { bottom: -6 },
  bn7: { bottom: -7 },
  bn8: { bottom: -8 },
  bn9: { bottom: -9 },
  bn10: { bottom: -10 },

  // grid system
  row: {
    flexDirection: 'row'
  },
  colFull: {
    width: '100%'
  },
  colHalf: {
    width: '48%'
  },
  colThird: {
    width: '31%'
  },
  colThirdLast: {
    width: '30%'
  },
  colLeft: {
    marginRight: '2%'
  },
  colRight: {
    marginLeft: '2%'
  },
  shiftHeader: {
    backgroundColor: '#9DD086',
    width: '60%',
    alignItems: 'center',
    marginTop: -18,
    padding: 5,
    borderBottomEndRadius: 15,
    borderBottomLeftRadius: 15,
    left: '20%'
  },
  shiftTitle: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center'
  },

  hContainer: { flexDirection: 'row' },

  calendarDaysView: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    marginTop: 0
  },

  shiftInfo: {
    textAlign: 'center',
    color: '#0088CF',
    marginBottom: 10,
    fontFamily: fontPalette.primaryRegular
  },

  centerAlign: { justifyContent: 'center', alignItems: 'center' },

  title: {
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 16,
    marginTop: 0
  },

  calendarDay: {
    height: 35,
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 0,
    fontSize: 18
  },

  activeShiftClick: {
    backgroundColor: '#CDE8EA',
    padding: 10,
    borderRadius: 30
  },
  searchInput: { padding: 10, backgroundColor: '#E0E0E0', borderRadius: 10 },
  customInput: {
    padding: 10,
    borderBottomColor: '#c3c3c3',
    borderBottomWidth: 1
  },
  customInputBorder: {
    padding: 10,
    borderWidth: 1,
    borderColor: '#c3c3c3',
    borderRadius: 10
  }
})

export { globalStyles, colorPalette, fontPalette }
